import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import Button from '../../components/ui/Button/Button'
import DevolucionOrderDetail from '../../components/devoluciones/DevolucionOrderDetail'
import DevolucionOrderList from '../../components/devoluciones/DevolucionOrderList'
import DevolucionUnitsTable from '../../components/devoluciones/DevolucionUnitsTable'
import { useAuth } from '../../contexts/AuthContext'
import { useCoberturaTraslados } from '../../hooks/devoluciones/useCoberturaTraslados'
import { useDevolucionOrders } from '../../hooks/devoluciones/useDevolucionOrders'
import { useDevolucionTraslado } from '../../hooks/devoluciones/useDevolucionTraslado'
import {
  ESTADOS_DEVOLUCIONES_PARA_TRANSFERIR,
  type DevolucionUnitSelections,
  type EstadoUnidadDevolucion
} from '../../types/devoluciones'
import {
  everyTransferableUnitHasSelection,
  expandDevolucionLineItems,
  getTransferableUnits
} from '../../utils/devoluciones-utils'

const BASE_PATH = '/devoluciones/verificadas'

const DevolucionesVerificadas: React.FC = () => {
  const { id } = useParams()
  const { hasPermission } = useAuth()
  const canTrasladar = hasPermission('manage_wms_traslados')
  const observer = useRef<IntersectionObserver | null>(null)
  const [selections, setSelections] = useState<DevolucionUnitSelections>({})

  const {
    orders,
    selectedOrder,
    page,
    setPage,
    hasMore,
    isLoading,
    isLoadingMore,
    fetchOrderById
  } = useDevolucionOrders(ESTADOS_DEVOLUCIONES_PARA_TRANSFERIR)
  const { isSubmitting, trasladar } = useDevolucionTraslado()
  const {
    cobertura,
    isLoading: isLoadingCobertura,
    error: coberturaError,
    refetch: refetchCobertura
  } = useCoberturaTraslados(selectedOrder?.id)

  const coberturaItems = useMemo(() => cobertura?.items || [], [cobertura?.items])
  const coberturaTransfers = useMemo(() => cobertura?.transfers || [], [cobertura?.transfers])
  const hasCoberturaReady = Boolean(selectedOrder) && !isLoadingCobertura && !coberturaError && cobertura != null

  const units = useMemo(
    () => expandDevolucionLineItems(selectedOrder?.lineItems || []),
    [selectedOrder?.lineItems]
  )

  const pendingTransfers = useMemo(
    () => coberturaTransfers.filter(
      (transfer) => String(transfer.estado || '').toLowerCase() === 'pendiente'
    ),
    [coberturaTransfers]
  )
  const approvedTransfers = useMemo(
    () => coberturaTransfers.filter(
      (transfer) => String(transfer.estado || '').toLowerCase() === 'aprobado'
    ),
    [coberturaTransfers]
  )
  const hasPendingTransfer = pendingTransfers.length > 0
  const hasApprovedTransfer = approvedTransfers.length > 0
    || (cobertura?.productos_con_transferencia_aprobada?.length ?? 0) > 0
    || coberturaItems.some((item) => item.hasApprovedTransfer || item.transferencia_aprobada || item.fullyApproved)

  const selectableUnits = useMemo(
    () => (hasCoberturaReady ? getTransferableUnits(units, coberturaItems, coberturaTransfers) : []),
    [hasCoberturaReady, units, coberturaItems, coberturaTransfers]
  )
  const isReadyToTrasladar = hasCoberturaReady && everyTransferableUnitHasSelection(
    units,
    selections,
    coberturaItems,
    coberturaTransfers
  )
  const canCreateAnyTransfer = hasCoberturaReady && cobertura?.canCreateAnyTransfer !== false

  useEffect(() => {
    setSelections({})
  }, [selectedOrder?.id])

  useEffect(() => {
    if (id && String(selectedOrder?.id || '') !== id) {
      fetchOrderById(id)
    }
  }, [fetchOrderById, id, selectedOrder?.id])

  const lastOrderElementRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return
    if (observer.current) observer.current.disconnect()
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        setPage((currentPage) => currentPage + 1)
      }
    })
    if (node) observer.current.observe(node)
  }, [hasMore, isLoading, isLoadingMore, setPage])

  const handleSelectionChange = (unitId: string, value: EstadoUnidadDevolucion) => {
    setSelections((current) => ({
      ...current,
      [unitId]: value
    }))
  }

  const handleTrasladar = async () => {
    if (!selectedOrder) {
      toast.error('Selecciona un pedido para transferir')
      return
    }
    if (!hasCoberturaReady) {
      toast.error('No se pudo consultar la cobertura de transferencias. Verifique las conexiones.')
      return
    }
    if (!canCreateAnyTransfer) {
      toast.error(
        hasPendingTransfer
          ? 'Este pedido ya tiene una transferencia pendiente'
          : 'No se puede crear más transferencia para este pedido'
      )
      return
    }
    if (!selectableUnits.length) {
      toast.error('No hay unidades disponibles para transferir')
      return
    }
    if (!isReadyToTrasladar) {
      toast.error('Selecciona Sí o No en todas las unidades disponibles')
      return
    }

    const success = await trasladar(selectedOrder, selectableUnits, selections)
    if (success) {
      setSelections({})
      await refetchCobertura(selectedOrder.id)
    }
  }

  return (
    <div className="flex h-full">
      <div
        className="w-56 overflow-y-auto border-r border-gray-200 bg-white"
        style={{ maxHeight: 'calc(100vh - 64px)' }}
      >
        <div className="p-4">
          <h4 className="mb-3 font-semibold text-blue-800">Devoluciones para transferir</h4>
          <DevolucionOrderList
            orders={orders}
            basePath={BASE_PATH}
            isLoading={isLoading && page === 1}
            isLoadingMore={isLoadingMore}
            emptyMessage="No hay pedidos en estados devolucion_verificada, devolucion_incompleta, devolucion_incompleta_por_nc o devolucion_para_transferir"
            onSelectOrder={fetchOrderById}
            lastOrderRef={lastOrderElementRef}
          />
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto bg-gray-50 p-6"
        style={{ maxHeight: 'calc(100vh - 64px)' }}
      >
        <DevolucionOrderDetail order={selectedOrder}>
          {selectedOrder && (
            <>
              {isLoadingCobertura && (
                <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
                  Consultando estado de transferencias...
                </div>
              )}

              {!isLoadingCobertura && coberturaError && (
                <div
                  className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800"
                  role="alert"
                >
                  No se pudo consultar la cobertura de transferencias. Verifique las conexiones.
                  {coberturaError ? ` (${coberturaError})` : ''}
                </div>
              )}

              {!isLoadingCobertura && !coberturaError && hasPendingTransfer && (
                <div
                  className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900"
                  role="status"
                >
                  Este pedido ya tiene{' '}
                  {pendingTransfers.length === 1
                    ? 'una transferencia pendiente'
                    : `${pendingTransfers.length} transferencias pendientes`}
                  {pendingTransfers[0]?.consecutivo
                    ? ` (${pendingTransfers.map((t) => t.consecutivo || `#${t.id}`).join(', ')})`
                    : ''}
                  .
                  {!canCreateAnyTransfer
                    ? ' Por eso no se puede crear otra transferencia por ahora.'
                    : ''}
                </div>
              )}

              {!isLoadingCobertura && !coberturaError && !hasPendingTransfer && hasApprovedTransfer && cobertura?.canCreateAnyTransfer === false && (
                <div
                  className="mb-4 rounded-lg border border-green-200 bg-green-50 p-4 text-green-900"
                  role="status"
                >
                  Este pedido ya tiene la cantidad cubierta en transferencias aprobadas
                  {approvedTransfers[0]?.consecutivo
                    ? ` (${approvedTransfers.map((t) => t.consecutivo || `#${t.id}`).join(', ')})`
                    : ''}
                  .
                </div>
              )}

              {!isLoadingCobertura && !coberturaError && !hasPendingTransfer && !hasApprovedTransfer && cobertura?.canCreateAnyTransfer === false && (
                <div
                  className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900"
                  role="status"
                >
                  Este pedido ya tiene la cantidad cubierta en transferencias pendiente/aprobado.
                  No se puede crear otra transferencia por ahora.
                </div>
              )}

              <DevolucionUnitsTable
                units={units}
                selections={selections}
                onChange={handleSelectionChange}
                disabled={isSubmitting || !canTrasladar || !hasCoberturaReady}
                coberturaItems={hasCoberturaReady ? coberturaItems : []}
                coberturaTransfers={hasCoberturaReady ? coberturaTransfers : []}
              />

              <div className="flex flex-col items-end gap-3">
                {!canTrasladar && (
                  <p className="text-sm text-yellow-700">
                    No tienes permisos para crear transferencias Flexy.
                  </p>
                )}
                {coberturaError && (
                  <p className="text-sm text-red-700">
                    No se puede transferir hasta verificar las conexiones de cobertura.
                  </p>
                )}
                <Button
                  type="button"
                  variant="success"
                  onClick={handleTrasladar}
                  disabled={
                    !canTrasladar ||
                    !hasCoberturaReady ||
                    !canCreateAnyTransfer ||
                    !isReadyToTrasladar ||
                    selectableUnits.length === 0
                  }
                  isLoading={isSubmitting || isLoadingCobertura}
                >
                  Transferir
                </Button>
              </div>
            </>
          )}
        </DevolucionOrderDetail>
      </div>
    </div>
  )
}

export default DevolucionesVerificadas
